import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { cn } from '../../../utils/cn';
import { contentService } from '../../../services/contentService';
import { useLanguage } from '../../../contexts/LanguageContext';

const MAX_IMAGES = 10;
const AUTOPLAY_INTERVAL = 5000;
const SWIPE_THRESHOLD = 50;

function preload(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(src);
    img.onerror = reject;
    img.src = src;
  });
}

function useDiscoveredImages() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isCancelled = false;

    async function discover() {
      // 1. Try Cloudinary images from MongoDB first
      try {
        const response = await contentService.getCarouselImages();
        if (response.success && response.data.length > 0) {
          const cloudinaryImages = response.data
            .sort((a, b) => a.order - b.order)
            .map(item => item.image);

          if (!isCancelled) {
            setImages(cloudinaryImages);
            setLoading(false);
            return;
          }
        }
      } catch (err) {
        console.warn('Cloudinary images not available, falling back to local images');
      }

      // 2. Fallback to local public/img/carousel images
      const candidates = [];
      for (let i = 0; i < MAX_IMAGES; i++) {
        candidates.push(`/img/carousel/carousel_${i}.png`);
        candidates.push(`/img/carousel/carousel_image${i}.png`);
      }

      const found = [];
      const checks = candidates.map((src) =>
        preload(src)
          .then(() => found.push(src))
          .catch(() => null)
      );
      await Promise.all(checks);

      if (!isCancelled) {
        found.sort();
        setImages(found);
        setLoading(false);
      }
    }

    discover();
    return () => {
      isCancelled = true;
    };
  }, []);

  return { images, loading };
}

/**
 * Hero carousel component with auto-play and touch support
 */
const HeroCarousel = () => {
  const { images, loading } = useDiscoveredImages();
  const { t } = useLanguage();
  const [index, setIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const containerRef = useRef(null);
  const touchStartX = useRef(null);
  const touchDeltaX = useRef(0);

  const hasSlides = images.length > 0;
  const totalSlides = images.length;
  const safeIndex = useMemo(
    () => (hasSlides ? ((index % totalSlides) + totalSlides) % totalSlides : 0),
    [index, totalSlides, hasSlides]
  );

  const next = useCallback(() => {
    if (hasSlides) setIndex((i) => (i + 1) % totalSlides);
  }, [hasSlides, totalSlides]);

  const prev = useCallback(() => {
    if (hasSlides) setIndex((i) => (i - 1 + totalSlides) % totalSlides);
  }, [hasSlides, totalSlides]);

  const goTo = useCallback((i) => {
    if (hasSlides) setIndex(i);
  }, [hasSlides]);

  // Autoplay
  useEffect(() => {
    if (!hasSlides || isHovered || isPaused) return;

    const id = setInterval(next, AUTOPLAY_INTERVAL);
    return () => clearInterval(id);
  }, [hasSlides, isHovered, isPaused, next]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        prev();
        setIsPaused(true);
      } else if (e.key === 'ArrowRight') {
        next();
        setIsPaused(true);
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('keydown', handleKeyDown);
      return () => container.removeEventListener('keydown', handleKeyDown);
    }
  }, [prev, next]);

  // Touch/Swipe handlers
  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches?.[0]?.clientX ?? e.clientX;
    touchDeltaX.current = 0;
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (touchStartX.current == null) return;
    const currentX = e.touches?.[0]?.clientX ?? e.clientX;
    touchDeltaX.current = currentX - touchStartX.current;
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (touchDeltaX.current > SWIPE_THRESHOLD) {
      prev();
    } else if (touchDeltaX.current < -SWIPE_THRESHOLD) {
      next();
    }
    touchStartX.current = null;
    touchDeltaX.current = 0;
  }, [prev, next]);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  return (
    <div
      className="relative w-full h-[calc(100vh-88px)] min-h-[500px] overflow-hidden bg-gray-900 group"
      role="region"
      aria-roledescription="carousel"
      aria-label={t('hero.featured')}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      tabIndex={0}
    >
      <div className="relative w-full h-full" aria-live="off">
        {/* Loading State */}
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-800" aria-label={t('hero.loading')}>
            <div className="w-full h-full bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 animate-pulse" />
          </div>
        )}

        {/* Empty State */}
        {!loading && !hasSlides && (
          <div className="absolute inset-0 flex items-center justify-center text-white text-center p-8" role="alert">
            <p className="text-lg">
              {t('hero.noImages')}
            </p>
          </div>
        )}

        {/* Slides */}
        {!loading && hasSlides && (
          images.map((src, i) => (
            <div
              key={src}
              className={cn(
                'absolute inset-0 w-full h-full transition-opacity duration-1000 ease-in-out',
                i === safeIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
              )}
              role="group"
              aria-roledescription="slide"
              aria-label={t('hero.slide', { 0: i + 1, 1: totalSlides })}
              aria-hidden={i !== safeIndex}
            >
              <div className={cn(
                "absolute inset-0 w-full h-full transition-transform duration-[10000ms] ease-linear",
                i === safeIndex && !isPaused ? "scale-110" : "scale-100"
              )}>
                <img
                  src={src}
                  alt={t('hero.carouselImage', { 0: i + 1 })}
                  className="w-full h-full object-cover"
                  loading={i === 0 ? 'eager' : 'lazy'}
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/30" />

              {/* Optional enhancements: Add text overlay if needed here */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center text-white px-4 transform transition-all duration-1000 delay-300 translate-y-0 opacity-100"
                  style={{ opacity: i === safeIndex ? 1 : 0, transform: i === safeIndex ? 'translateY(0)' : 'translateY(20px)' }}>
                  {/* Placeholder for dynamic slide content if available later */}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Controls */}
      {!loading && hasSlides && (
        <>
          {/* Previous Button */}
          <button
            type="button"
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 active:bg-white/40 flex items-center justify-center transition-all touch-manipulation"
            aria-label={t('hero.prev')}
            onClick={prev}
          >
            <FiChevronLeft size={20} className="sm:w-6 sm:h-6 text-white" aria-hidden="true" />
          </button>

          {/* Next Button */}
          <button
            type="button"
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 active:bg-white/40 flex items-center justify-center transition-all touch-manipulation"
            aria-label={t('hero.next')}
            onClick={next}
          >
            <FiChevronRight size={20} className="sm:w-6 sm:h-6 text-white" aria-hidden="true" />
          </button>

          {/* Indicators */}
          <div
            className="absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-1.5 sm:gap-2 px-4"
            role="tablist"
            aria-label={t('hero.selector')}
          >
            {images.map((_, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={i === safeIndex}
                aria-label={t('hero.goTo', { 0: i + 1 })}
                aria-controls={`slide-${i}`}
                className={cn(
                  'h-2.5 sm:h-3 rounded-full transition-all touch-manipulation',
                  i === safeIndex
                    ? 'bg-white w-6 sm:w-8'
                    : 'bg-white/50 hover:bg-white/70 active:bg-white w-2.5 sm:w-3'
                )}
                onClick={() => goTo(i)}
                tabIndex={i === safeIndex ? 0 : -1}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default HeroCarousel;
